import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import helvetiker from './fonts/helvetiker_regular.typeface.json';

// Pre-parse font & cache 3D text geometry once at module import time.
// This removes the ~0.5s main-thread JSON parsing and 3D mesh triangulation delay during component mount!
let cachedTextGeometry = null;

const getCachedTextGeometry = () => {
  if (!cachedTextGeometry) {
    const loader = new FontLoader();
    const font = loader.parse(helvetiker);

    cachedTextGeometry = new TextGeometry('Swamp Yankee Tech', {
      font: font,
      size: 60,
      depth: 10,
      curveSegments: 8,
      bevelEnabled: true,
      bevelThickness: 2.5,
      bevelSize: 1.5,
      bevelOffset: 0,
      bevelSegments: 4,
    });
    cachedTextGeometry.center();
  }
  return cachedTextGeometry;
};

export const TitleImage = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    // Scene
    const scene = new THREE.Scene();

    // Camera
    const camera = new THREE.OrthographicCamera(-400, 400, 45, -45, 0.1, 1000);
    camera.position.set(0, 0, 500);

    // Renderer with WebGL context error safety for headless test environments
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      container.appendChild(renderer.domElement);
    } catch (e) {
      console.warn('WebGL not supported or context creation failed:', e);
      return;
    }

    // Instantly retrieve cached geometry (0ms delay)
    const textGeometry = getCachedTextGeometry();

    // White Material with high specular shine
    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.2,
      metalness: 0.1,
    });

    const textMesh = new THREE.Mesh(textGeometry, material);
    textMesh.rotation.set(0, 0, 0);
    scene.add(textMesh);

    // Lighting setup for crisp 3D bevels & highlights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);

    const dirLightTop = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLightTop.position.set(5, 10, 10);
    scene.add(dirLightTop);

    const dirLightBottom = new THREE.DirectionalLight(0x888888, 0.6);
    dirLightBottom.position.set(-5, -10, -5);
    scene.add(dirLightBottom);

    // Responsiveness handler for narrow screens
    const handleResize = () => {
      if (!container) return;
      const width = container.clientWidth;
      const height = Math.max(120, Math.min(width * 0.45, 200));

      renderer.setSize(width, height);
      camera.aspect = width / height;

      // Scale text down responsively on narrow screens
      const scale = Math.min(width / 520, 1.0);
      textMesh.scale.set(scale, scale, scale);

      camera.updateProjectionMatrix();
    };

    handleResize();

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(container);
    window.addEventListener('resize', handleResize);

    // Render loop (Continuous rotation around the horizontal X-axis)
    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      textMesh.rotation.x -= 0.03; // Rotate around horizontal X-axis
      renderer.render(scene, camera);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrameId);
      if (renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div className="Title-wrapper">
      <div
        className="Title-three-container"
        ref={mountRef}
        style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '0 auto' }}
      />
      <p>
        [ tech only a swamp yankee could love ]
      </p>
    </div>
  );
};

export function UserButton({ button_name, do_something }) {
  return (
    <button className="User-button" onClick={do_something}>
      {button_name}
    </button>
  );
};

export function InputTextField(label_value, name_value, value, handleChange) {
  return (
    <div className="Input-params">
      <label className="Form-label">{label_value}:</label>
      <input className="Form-input" type="text" name={name_value} value={value || ""} onChange={handleChange} />
    </div>
  );
};

export function InputNumField(label_value, name_value, value, handleChange) {
  return (
    <div className="Input-params">
      <label className="Form-label">{label_value}:</label>
      <input className="Form-input" type="number" name={name_value} value={value || ""} onChange={handleChange} />
    </div>
  );
};

